import { CircleAlert, Eye, EyeOff, FileIcon, RefreshCcw } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAttachmentMetadata } from '@/billing/hooks/useAttachmentMetadata';
import { Badge } from '@/common/components/ui/badge';
import { Button } from '@/common/components/ui/button';
import { Skeleton } from '@/common/components/ui/skeleton';
import { cn } from '@/lib/utils';

type DocumentItemProps = {
  attachmentId: number;
  openedDocumentId?: number;
  onToggleDocument: (attachmentId: number) => void;
  canRename?: boolean;
  onRename?: () => void;
  children?: ReactNode;
};

const DocumentItem = ({
  attachmentId,
  openedDocumentId,
  onToggleDocument,
  canRename,
  onRename,
  children,
}: DocumentItemProps) => {
  const { t } = useTranslation();
  const { metadata, isFetching, isError, refetch } =
    useAttachmentMetadata(attachmentId);

  const isDocumentOpen = openedDocumentId === attachmentId;
  const showError = isError && !isFetching;

  return (
    <li
      className={cn(
        'border rounded-lg px-3 py-2.5 flex flex-col gap-2',
        children ? 'border-primary bg-background' : 'bg-muted',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {showError ? (
            <CircleAlert className="size-4 shrink-0 mt-0.5 text-destructive" />
          ) : (
            <FileIcon className="size-4 shrink-0 mt-0.5" />
          )}

          <div className="flex flex-col min-w-0">
            {isFetching ? (
              <Skeleton
                role="status"
                className="h-4 w-40 my-0.5 bg-muted-foreground/20"
              />
            ) : (
              <span className="truncate text-sm font-medium">
                {metadata?.description || attachmentId}
              </span>
            )}

            {showError && (
              <span role="alert" className="text-xs text-destructive">
                {t('billing.drawer.attachmentMetadataError')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0 shrink-0">
          {showError && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('billing.drawer.retry')}
              onClick={() => refetch()}
            >
              <RefreshCcw className="stroke-ring" />
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="h-min hover:bg-transparent"
            aria-label={
              isDocumentOpen
                ? t('billing.drawer.document.hide')
                : t('billing.drawer.document.view')
            }
            onClick={() => onToggleDocument(attachmentId)}
          >
            {isDocumentOpen ? (
              <EyeOff className="size-5 stroke-ring" />
            ) : (
              <Eye className="size-5 stroke-ring" />
            )}
          </Button>
        </div>
      </div>

      {children || (
        <div className="flex items-center justify-between">
          <Badge variant="gray">{t('billing.drawer.document.toRename')}</Badge>
          {canRename && !isError && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onRename}
            >
              {t('billing.drawer.rename.action')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

export default DocumentItem;
